/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

/**
 * auth-client.ts
 *
 * Allauth headless HTTP client + React hook layer.
 *
 * Allauth headless base path: /_allauth/app/v1
 *
 * Session tokens are returned in body.meta.session_token (new sessions — e.g. OTP)
 * or the X-Session-Token response header (existing sessions). Both are stored in
 * localStorage and sent back via X-Session-Token on every subsequent request.
 *
 * Google OAuth uses allauth's browser-mode social login (form POST to
 * /_allauth/browser/v1/auth/provider/redirect). App-mode provider/redirect
 * is for native apps — it does not handle browser redirect flows.
 */

import { useEffect, useRef, useState } from "react";
import { configuration } from "./configuration";
import i18n from "@/i18n";
import { safeLocalStorage } from "@/helper/safeLocalStorage";

const BASE = configuration.backendURL;
const ALLAUTH_BASE = `${BASE}/_allauth/app/v1`;
/**
 * The localStorage key holding the allauth session key.
 *
 * Exported (F-3) so a listener for the browser's `storage` event can tell a
 * change to THIS key from a change to any other. That event fires for every
 * key any other tab writes — cookie preferences, the language preference, the
 * forum flag — and without the filter every one of them would trigger a token
 * mint.
 */
export const SESSION_KEY = "ds_session_token";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  emailVerified: boolean;
  image: string | null;
}

export interface AuthSession {
  token: string;
  userId: string;
}

/**
 * One error from the account library, in the shape the pages read it.
 *
 * Session 383 S-4: `code` is new, and it is the whole of that fix. A lockout
 * and a wrong password are BOTH an HTTP 400 with no `param`, so the status
 * cannot tell them apart and the message the customer saw was "Incorrect email
 * or password." — which is not only unhelpful, it is untrue: the password may
 * well have been right, because the attempt limit is consumed before the
 * password is even checked.
 */
export interface AuthError {
  message: string;
  status: number;
  param?: string;
  /** The server's own error code, for conditions that share an HTTP status. */
  code?: string;
}

export interface AuthResult<T = void> {
  data?: T;
  error?: AuthError;
}

export interface SignInResult {
  user: AuthUser;
  session: AuthSession;
  meta?: { is_authenticated: boolean };
}

export interface SessionResult {
  user: AuthUser;
  session: AuthSession;
}

// ---------------------------------------------------------------------------
// Token storage
// ---------------------------------------------------------------------------

export function getSessionToken(): string | null {
  // N-4 (Session 447): storage access throws outright when site data is
  // blocked, in some private modes, in a partitioned third-party context and
  // in an embedded webview whose host restricts it - which is precisely the
  // environment the In-App Purchase sign-in report came from. A refusal must
  // read as "no session", which every caller here already handles, rather
  // than as an exception thrown through a sign-in handler that has no catch.
  //
  // Both consequences of the unguarded read were bad and neither was
  // diagnosable: inside allauthFetch the throw was caught by that function's
  // broad catch and reported to the customer as "Network error. Please check
  // your connection." after a sign-in the server had actually completed;
  // inside redirectWithAuthToken there was no catch at all.
  return safeLocalStorage.getItem(SESSION_KEY);
}

/**
 * F-3: fired whenever the stored session key is written or cleared.
 *
 * The support chat listens for this so a visitor who signs in or out with the
 * panel open stops or starts being recognised without the panel resetting.
 *
 * Exported as a constant rather than written as a string at each end, because
 * a name that exists twice is a name that can drift, and a mismatch here
 * produces no error anywhere — the listener simply never fires.
 */
export const SESSION_CHANGED_EVENT = "ds:session-changed";

function announceSessionChanged(): void {
  try {
    window.dispatchEvent(new Event(SESSION_CHANGED_EVENT));
  } catch {
    // Non-browser environment — nothing is listening.
  }
}

/*
 * F-3: the announcement lives inside these two helpers, not at the call sites.
 *
 * The session key is written in two places in this module and cleared in five —
 * a failed token refresh in two branches, an OTP request that clears a stale
 * key before signing in, sign-out, and account deletion. Every one of them goes
 * through these two helpers, so announcing here covers all seven automatically
 * and no future call site can forget it. All seven are correct occasions to
 * tell the chat: in each case there is genuinely no session afterwards, which
 * is exactly what the signed-out message says.
 */
function storeSessionToken(token: string): boolean {
  // N-4: whether the write succeeded is returned rather than discarded. A
  // sign-in whose session key could not be stored has not really succeeded,
  // and saying so is what lets the page show an accurate message instead of
  // sending the customer away to check their connection.
  const stored = safeLocalStorage.setItem(SESSION_KEY, token);
  announceSessionChanged();
  return stored;
}

/**
 * Persist the allauth session key after the OAuth browser hand-off (T-5).
 *
 * The write path is otherwise private to this module. Google OAuth is the one
 * flow that receives its session key through a redirect rather than a JSON
 * response, so it needs a way in.
 */
export function setSessionToken(token: string): boolean {
  return storeSessionToken(token);
}

function clearSessionToken(): void {
  safeLocalStorage.removeItem(SESSION_KEY);
  announceSessionChanged();
}

// ---------------------------------------------------------------------------
// Core fetch wrapper
// ---------------------------------------------------------------------------

const FETCH_TIMEOUT_MS = 30_000;

async function allauthFetch<T>(
  path: string,
  options: RequestInit = {},
  _retried = false,
): Promise<AuthResult<T>> {
  const token = getSessionToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "application/json",
    // S-2: Send the site's active language so LocaleMiddleware picks the
    // correct locale for transactional emails (sign-in code, password reset).
    // i18n.language is restricted to the supported set (en/de/es/fr/it/pt)
    // via supportedLngs, which maps 1:1 to the backend LANGUAGES setting —
    // no extra normalization needed.
    "Accept-Language": i18n.language || "en",
    ...(token ? { "X-Session-Token": token } : {}),
    ...(options.headers as Record<string, string>),
  };

  // Ensure CSRF token header for unsafe methods when credentials are included.
  const method = ((options.method ?? "GET") as string).toUpperCase();
  if (method !== "GET" && !headers["X-CSRFToken"]) {
    // Try cookie first (same-origin dev), otherwise call the CSRF endpoint.
    const existing = getCsrfCookie();
    const csrf = existing || (await fetchCsrfToken());
    if (csrf) headers["X-CSRFToken"] = csrf;
  }

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

  try {
    const response = await fetch(`${ALLAUTH_BASE}${path}`, {
      ...options,
      headers,
      credentials: "include",
      signal: controller.signal,
    });

    // Parse body first — token lives in body.meta.session_token for new sessions
    // (e.g. OTP request) and in the X-Session-Token header for existing sessions.
    const body =
      response.ok || response.status < 500
        ? await response.json().catch(() => ({}))
        : {};

    // U-13: the console.debug that used to sit here has been deleted. It wrote
    // the whole parsed body of every non-OK /_allauth/ response to the console
    // - every failed login, every rejected password reset, every rate-limited
    // attempt, and whatever those bodies carry after any future change to a
    // serializer or an adapter.
    //
    // The guard was import.meta.env.MODE, a build-time flag that is
    // "production" only for a plain `vite build`, so a feature or staging
    // image built with any other --mode carried it - and feature environments
    // are exactly where testers sign in with real-looking credentials. Sentry's
    // default breadcrumbs integration captures console calls, so each line was
    // attached to the next error event and left the machine. main.tsx now drops
    // console breadcrumbs outright (see beforeBreadcrumb), which closes the
    // class of fault as well as this instance.
    //
    // Nothing is lost: the status and the error code are already returned to
    // the caller, and the whole exchange is already visible in the Network
    // panel without any logging at all.

    // Primary: body.meta.session_token. Fallback: header (existing/modified sessions).
    const metaToken = (body as { meta?: { session_token?: string } })?.meta
      ?.session_token;
    const headerToken = response.headers.get("X-Session-Token");
    const newToken = metaToken ?? headerToken;
    // N1: Never save a token returned by the logout response. The server's
    // DELETE /auth/session reply can carry a fresh signed-out token in the
    // same header. Saving it re-populates localStorage right after we cleared
    // it, causing the login screen to flicker back to a signed-in look (S1).
    const isLogout =
      path === "/auth/session" &&
      (options.method ?? "GET").toUpperCase() === "DELETE";
    if (newToken && !isLogout) {
      storeSessionToken(newToken);
    }

    if (!response.ok) {
      // 410 Gone: the session token no longer exists on the server.
      // Clear the stale token and retry once as a clean unauthenticated request.
      if (response.status === 410 && !_retried) {
        clearSessionToken();
        return allauthFetch<T>(path, options, true);
      }

      // 409 Conflict on /auth/login: a pending-OTP session is blocking a
      // new password login. Clear the stale token and retry once.
      // T2: Never retry 409 on /auth/password/reset — the reset key is a
      // one-time token. A retry can consume the key on the first attempt and
      // fail on the second, showing "expired or invalid" after a correct
      // single submission. resetPassword() already clears the token before its
      // single POST so any stale-session 409 is handled the safe way.
      if (response.status === 409 && !_retried && path === "/auth/login") {
        clearSessionToken();
        return allauthFetch<T>(path, options, true);
      }

      const b = body as Record<string, unknown>;

      // Allauth headless contract — 401 with a flows[] array signals a
      // pending authentication state (OTP sent, MFA required, etc.) — NOT a failure.
      // The flows array can sit at the top level or inside a data key.
      const inner = b?.data as Record<string, unknown> | undefined;
      const flows = Array.isArray(b?.flows)
        ? b.flows
        : /* c8 ignore next 2 -- nested ternary; "flows inside data key" test covers
             this path but c8 cannot track inner branch of a nested ternary */
          Array.isArray(inner?.flows)
          ? inner.flows
          : null;

      /* c8 ignore next 3 -- 401+flows branch is tested but c8 undercounts branches
         when a ternary result immediately feeds the if condition */
      if (response.status === 401 && flows) {
        return { data: body as T };
      }

      const message =
        (b?.errors as Array<{ message: string }>)?.[0]?.message ??
        (b?.error as string) ??
        (b?.detail as string) ??
        "An error occurred. Please try again.";
      // Allauth includes a structured "param" field on validation errors
      // (e.g. param="password" for password-rule failures on password reset).
      // Carry it through so callers can key on it rather than parsing the message.
      const param = (b?.errors as Array<{ param?: string }>)?.[0]?.param;
      // Session 383 S-4: the code was being dropped here and never reached the
      // page. It is the only thing that distinguishes an exhausted attempt
      // limit (too_many_login_attempts) from a wrong password: both arrive as
      // an ordinary 400 with no param, so every attempt from the sixth onward
      // told the customer their password was wrong.
      const code = (b?.errors as Array<{ code?: string }>)?.[0]?.code;
      return {
        error: {
          message,
          status: response.status,
          ...(param ? { param } : {}),
          ...(code ? { code } : {}),
        },
      };
    }

    return { data: body as T };
  } catch (err) {
    if ((err as Error).name === "AbortError") {
      return {
        error: {
          message: "Request timed out. Please check your connection.",
          status: 0,
        },
      };
    }
    return {
      error: {
        message: "Network error. Please check your connection.",
        status: 0,
      },
    };
  } finally {
    clearTimeout(timer);
  }
}

// ---------------------------------------------------------------------------
// Session fetch (shared by hook + one-shot callers)
// ---------------------------------------------------------------------------

async function fetchSession(): Promise<AuthResult<SessionResult>> {
  const raw = await allauthFetch<Record<string, unknown>>("/auth/session", {
    method: "GET",
  });

  if (raw.error) return { error: raw.error };
  const body = raw.data as Record<string, unknown>;

  const inner = body?.data as Record<string, unknown> | undefined;
  const meta = body?.meta as Record<string, unknown> | undefined;
  const user = inner?.user as AuthUser | undefined;
  // Token is in meta.session_token — not in data.sessions[].
  // data.sessions belongs to allauth.usersessions, which is not installed.
  const token =
    (meta?.session_token as string | undefined) ?? getSessionToken();

  if (!user || !token) {
    return { error: { message: "No active session.", status: 401 } };
  }

  return { data: { user, session: { token, userId: String(user.id ?? "") } } };
}

// ---------------------------------------------------------------------------
// useSession hook
// ---------------------------------------------------------------------------

export interface UseSessionReturn {
  data: SessionResult | null;
  isPending: boolean;
  error: { message: string; status: number } | null;
}

export function useSession(): UseSessionReturn {
  const [state, setState] = useState<UseSessionReturn>({
    data: null,
    isPending: true,
    error: null,
  });

  const fetched = useRef(false);

  useEffect(() => {
    if (fetched.current) return;
    fetched.current = true;

    let cancelled = false;

    fetchSession().then((result) => {
      if (cancelled) return;
      if (result.data) {
        setState({ data: result.data, isPending: false, error: null });
      } else {
        setState({
          data: null,
          isPending: false,
          error: result.error ?? null,
        });
      }
    });

    return () => {
      cancelled = true;
      // Do not reset fetched.current here. The session fetch is idempotent,
      // but resetting the guard in cleanup causes a duplicate network call on
      // every React Strict Mode remount and keeps the code inconsistent with
      // the single-fire pattern used in one-time-code auth flows.
    };
  }, []);

  return state;
}

// ---------------------------------------------------------------------------
// Core auth functions
// ---------------------------------------------------------------------------

/**
 * T4: Drop any prior session token and Django browser cookie before a new
 * sign-in attempt. Called by every sign-in entry point that starts fresh
 * (password login, OTP request). Not called on the OTP confirm step — that
 * step relies on the pending-login token from the request step.
 *
 * Keeps sign-in consistent across all three entry paths:
 *   - Password login    → prepareForSignIn() clears token + browser cookie
 *   - OTP request       → prepareForSignIn() + existing clearSessionToken()
 *   - Google OAuth      → already calls browser-logout via signInWithGoogle()
 */
async function prepareForSignIn(): Promise<void> {
  clearSessionToken();
  try {
    await fetch(`${BASE}/api/auth/browser-logout`, {
      method: "POST",
      credentials: "include",
    });
  } catch {
    // Non-fatal — proceed regardless so a network blip doesn't block sign-in.
  }
}

async function signInWithEmail(
  email: string,
  password: string,
): Promise<AuthResult<SignInResult>> {
  // T4: Clear any prior session token and browser cookie before a password login.
  // A leftover session from a previous user can cause the server to authenticate
  // as that user rather than processing the new credentials.
  await prepareForSignIn();
  // Session 383 A-6: `login` is gone. ACCOUNT_LOGIN_METHODS on this service is
  // {"email"}, so the login input reads `email` and ignores `login` entirely —
  // sending both put the customer's address in the request body twice for no
  // purpose. The comment that justified it ("to satisfy different allauth
  // configurations") described a configuration this service does not have.
  return allauthFetch<SignInResult>("/auth/login", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
}

async function signInWithEmailOtp(
  otp: string,
): Promise<AuthResult<SignInResult>> {
  return allauthFetch<SignInResult>("/auth/code/confirm", {
    method: "POST",
    body: JSON.stringify({ code: otp }),
  });
}

async function signOut(): Promise<AuthResult<void>> {
  // T-2 (FCR Pass 8): call browser-logout first. This clears the Django
  // session cookie via Django's own logout() and — once the forum session
  // cookie exists (T-1) — will clear that in the same response too, which is
  // what makes a separate, unauthenticated forum-logout endpoint unnecessary.
  // Building that endpoint was rejected: it accepted a POST from any origin
  // with no auth, CSRF protection, or origin check, so any page on the
  // internet could silently end a customer's forum session. This call
  // achieves the same result — both sessions cleared together — through an
  // endpoint that already requires the customer's own authenticated request.
  try {
    await fetch(`${BASE}/api/auth/browser-logout`, {
      method: "POST",
      credentials: "include",
    });
  } catch {
    // Non-fatal — continue with the allauth session DELETE below regardless.
  }

  const result = await allauthFetch<void>("/auth/session", {
    method: "DELETE",
  });
  // Only clear the local token when the server confirms the session was
  // deleted. Clearing unconditionally means a failed DELETE leaves the user
  // appearing logged out locally while the server session is still active —
  // on reload useSession() re-authenticates them silently.
  //
  // Section 7.6: when the server call fails, return the error so the caller can
  // keep the sign-out button enabled for a retry. The local token is NOT
  // cleared, because the account session really is still live and clearing it
  // would leave the customer looking signed out while the server disagrees.
  //
  // A-21: be precise about the state this leaves behind. browser-logout has
  // ALREADY run by this point, so the Django session and the forum cookie are
  // gone. A failure here is partial, not total: the customer is signed out of
  // the forum but still signed in to their account. "Nothing happened, try
  // again" would be the wrong thing to tell them -- something did happen. The
  // retry is still the correct action, and it is safe to repeat, because
  // browser-logout is idempotent.
  if (!result.error) {
    clearSessionToken();
    // A-17: the forum link caches its resolution per session token. Clearing it
    // here means the next person to sign in on this browser gets their own
    // answer rather than inheriting the previous customer's.
    const { resetForumLinkCache } = await import("./forum-access");
    resetForumLinkCache();
    // T5: Clear the in-memory JWT cache immediately so a subsequent user
    // cannot be served User A's access token for up to 13 minutes after
    // User A signs out. The lazy clear inside getJsonWebToken() runs only
    // on the NEXT call, leaving the stale token in memory in the meantime.
    //
    // F-3 (Session 369): this already does what the chat item asked for, so
    // nothing was added here. Note the SHAPE of these two imports — they are
    // loaded at the moment they are used rather than at the top of the file,
    // because http-client imports getSessionToken from this module and a
    // top-level import here would close a cycle between two modules that both
    // load at start-up. Anything added to this function must follow the same
    // pattern; do not tidy them into ordinary imports.
    const { clearJwtCache } = await import("./http-client");
    clearJwtCache();
  }
  return result;
}

export async function getSession(): Promise<AuthResult<SessionResult>> {
  return fetchSession();
}

async function sendVerificationEmail(email: string): Promise<AuthResult> {
  // T4: A returning visitor may still hold a session token and a browser cookie
  // from an earlier visit. allauth rejects a fresh login-by-code request that
  // carries a valid session (400, no email sent). prepareForSignIn() clears both
  // the local token and the browser-side Django session cookie, so the request
  // is treated as the anonymous login-by-code request it is meant to be.
  // This also heals the "Resend code" fallback path: when resendLoginCode()
  // returns 409 (session stage expired), it falls back here, and that request
  // would fail the same way without this clear.
  await prepareForSignIn();
  // Send JSON body; headless allauth in this project expects JSON payloads.
  return allauthFetch("/auth/code/request", {
    method: "POST",
    body: JSON.stringify({ email }),
  });
}

async function forgotPassword(email: string): Promise<AuthResult> {
  return allauthFetch("/auth/password/request", {
    method: "POST",
    body: JSON.stringify({ email }),
  });
}

// Resend the active login code via POST /auth/code/resend (allauth 65.16+).
// Returns 409 if the session stage has expired (caller should fall back to sendVerificationEmail), 429 if rate-limited.
async function resendLoginCode(): Promise<AuthResult> {
  return allauthFetch("/auth/code/resend", {
    method: "POST",
  });
}

async function resetPassword(
  key: string,
  password: string,
): Promise<AuthResult> {
  // A stale session token from a prior login attempt can block the anonymous
  // password-reset flow (allauth may return 400/409). Drop it before posting.
  clearSessionToken();
  return allauthFetch("/auth/password/reset", {
    method: "POST",
    body: JSON.stringify({ key, password }),
  });
}

async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<AuthResult> {
  return allauthFetch("/auth/password/change", {
    method: "POST",
    body: JSON.stringify({
      current_password: currentPassword,
      new_password: newPassword,
    }),
  });
}

/**
 * Fetch CSRF token from the backend's /api/csrf/ endpoint.
 *
 * In cross-origin deployments (frontend on auth.deskshare.com, backend on
 * api.auth.deskshare.com), JavaScript cannot read cookies from a different
 * subdomain. getCsrfCookie() returns an empty string, and Django rejects the
 * Google OAuth form POST with 403.
 *
 * This function checks the cookie first (works in same-origin dev) and falls
 * back to the GET /api/csrf/ endpoint (works in all environments). The
 * endpoint sets the ds_csrf cookie via @ensure_csrf_cookie and returns the
 * token in the response body so we can read it cross-origin.
 */
function getCsrfCookie(): string {
  const match = document.cookie.match(/(?:^|;\s*)ds_csrf=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : "";
}

async function fetchCsrfToken(): Promise<string> {
  const cached = getCsrfCookie();
  if (cached) return cached;
  try {
    const res = await fetch(`${BASE}/api/csrf/`, {
      method: "GET",
      credentials: "include",
    });
    const data = (await res.json()) as { csrfToken?: string };
    return data.csrfToken ?? "";
  } catch {
    return "";
  }
}

/** Google sign-in via browser-mode social login (form POST to allauth's provider/redirect endpoint). */
async function signInWithGoogle(callbackUrl?: string): Promise<void> {
  // Flush the Django browser session (sessionid cookie) before starting the
  // OAuth flow. If a previous email/password login left a valid sessionid,
  // allauth's provider/redirect endpoint considers the user already
  // authenticated and short-circuits straight to callback_url without ever
  // redirecting to Google — the user never sees the Google sign-in screen.
  //
  // /api/auth/browser-logout is a @csrf_exempt POST that calls Django's
  // logout() directly — far more reliable than the allauth headless DELETE
  // endpoint which requires an authenticated session and a matching CSRF token
  // (causing 401s when the token/session mismatch).
  try {
    await fetch(`${BASE}/api/auth/browser-logout`, {
      method: "POST",
      credentials: "include",
    });
  } catch {
    // Non-fatal — proceed with the OAuth flow regardless.
  }

  const csrfToken = await fetchCsrfToken();
  const redirectUrl = callbackUrl ?? `${window.location.origin}/auth/callback`;

  const form = document.createElement("form");
  form.method = "POST";
  form.action = `${BASE}/_allauth/browser/v1/auth/provider/redirect`;
  form.style.display = "none";

  const fields: Record<string, string> = {
    provider: "google",
    callback_url: redirectUrl,
    process: "login",
    csrfmiddlewaretoken: csrfToken,
  };

  for (const [name, value] of Object.entries(fields)) {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value;
    form.appendChild(input);
  }

  document.body.appendChild(form);

  // The sign-in page stays in the back history behind Google's pages: the
  // form submission adds Google's page after it. That is what lets a customer
  // press Back on Google's account chooser and return to the sign-in page,
  // with Back to cart when the shop's purchase window opened it (VS-2). No
  // history call is made here — replacing this entry with itself would remove
  // nothing (Session 493, N-2).
  //
  // Google's own account-chooser page is an entry in the browser's history
  // and no code of ours can remove it. A customer pressing Back far enough
  // after signing in still reaches it, and Google signs them in again. That
  // is normal for every site using Google sign-in.
  form.submit();
}

function isAuthenticated(): boolean {
  return !!getSessionToken();
}

// ---------------------------------------------------------------------------
// authClient — better-auth compatible shape
// ---------------------------------------------------------------------------

export const authClient = {
  useSession,
  signOut,
  getSession,
  sendVerificationEmail,
  forgotPassword,
  resetPassword,
  changePassword,
  signInWithGoogle,
  isAuthenticated,
  resendLoginCode,

  signIn: {
    email: ({
      email,
      password,
    }: {
      email: string;
      password: string;
      callbackURL?: string;
    }): Promise<AuthResult<SignInResult>> => signInWithEmail(email, password),

    emailOtp: ({
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      email: _email, // intentionally unused — public API shape preserved; callers pass email but only otp is sent
      otp,
    }: {
      email: string;
      otp: string;
    }): Promise<AuthResult<SignInResult>> => signInWithEmailOtp(otp),

    social: ({
      provider,
      callbackURL,
    }: {
      provider: string;
      callbackURL?: string;
    }): Promise<AuthResult<void>> => {
      if (provider === "google") {
        // signInWithGoogle is async — return the Promise directly.
        return signInWithGoogle(callbackURL).then(
          () => ({}) as AuthResult<void>,
        );
      }
      return Promise.resolve({
        error: {
          message: `Social provider "${provider}" is not configured.`,
          status: 400,
        },
      } as AuthResult<void>);
    },
  },

  emailOtp: {
    sendVerificationOtp: ({
      email,
    }: {
      email: string;
      // type parameter intentionally omitted: only one code flow (sign-in)
      // exists. Wire it through here if additional code types are added.
    }): Promise<AuthResult> => sendVerificationEmail(email),
  },
};
